/*
================================================================================
File: src/pages/SellerLeads/EditLeadPageBlank.js
Description: A comprehensive form for creating a new seller lead with all fields.
================================================================================

*/
import React, { useState, useCallback } from "react";
import instance from "../../api/axios";
import { storage } from "../../App";
import { views } from "../../constants/views";
import PageContent from "../../components/common/PageContent";
import {
  FormGroup,
  FormGroupSingle,
  SelectGroup,
} from "../../components/common/FormGroup";
import {
  stateOptions,
  propertyTypeOptions,
  loanTypeOptions,
  boolOptions,
  foundationOptions,
  propertyConditionOptions,
  propertyOccupyOptions,
} from "../../constants/options";
import AutocompleteInput from "../../components/common/AutocompleteInput";
import Modal from "../../components/common/Modal";

// SVG Icons
const CalendarIcon = () => (<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>);
const MapPinIcon = () => (<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"/></svg>);
const UserIcon = () => (<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"/></svg>);
const ClockIcon = () => (<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>);
const ShieldIcon = () => (<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944A11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>);
const DollarIcon = () => (<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd"/></svg>);
const HomeIcon = () => (<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"/></svg>);
const DocumentIcon = () => (<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd"/></svg>);
const CreditCardIcon = () => (<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z"/><path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd"/></svg>);
const PenIcon = () => (<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"/></svg>);
const LinkIcon = () => (<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd"/></svg>);

const EditLeadPageBlank = ({ onViewChange, user, pulldata }) => {
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [newPhotoFiles, setNewPhotoFiles] = useState([]);
  const [newPhotoPreviews, setNewPhotoPreviews] = useState([]);
  const [isSAIConfirmOpen, setIsAIConfirmOpen] = useState(false);
  const [loading, setLoading] = useState({
    ai: false,
  });
  const [lead, setLead] = useState({
    // Initialize all fields to prevent uncontrolled component warnings
    owner: user.app_id,
    lead_date: new Date().toISOString().split("T")[0],
    input_source: "Snap Flip Bank",
    property_lead_name: "",
    property_address: "",
    property_city: "",
    property_state: "",
    property_zip: "",
    property_owner_name: "",
    mobile_number: "",
    email_address: "",
    secondary_contact: "",
    alternate_number: "",
    property_address_mailing: "",
    asking_price: "",
    need_to_sell_days: "30",
    needs_home: "0",
    property_listed: "0",
    property_isForeclosed: "0",
    insur_escrowed: "0",
    tax_escrowed: "0",
    other_escrowed: "0",
    total_debt: "",
    property_valued_price: "",
    property_repair_costs: "",
    property_type: "",
    propSubType: "",
    property_beds: "",
    property_baths: "",
    property_year_built: "",
    property_sqft: "",
    property_garage: "",
    property_stories: "",
    property_living_areas: "1",
    property_has_pool: "0",
    property_foundation: "Slab",
    property_is_occupied: "",
    property_condition: "",
    property_has_hoa: "0",
    bool_firstLoan: "",
    loan_1_origin: "",
    loan_1_loan_amount: "",
    loan_1_loan_intrest_rate: "",
    loan_1_loan_balance: "",
    loan_1_loan_term: "",
    loan_1_loan_type: "",
    loan_1_last_pay: "",
    loan_1_back_pay: "0",
    loan_1_lender: "",
    loan_1_loan_id: "",
    bool_secondLoan: "0",
    loan_2_origin: "",
    loan_2_loan_amount: "",
    loan_2_loan_intrest_rate: "",
    loan_2_loan_balance: "",
    loan_2_loan_term: "",
    loan_2_loan_type: "",
    loan_2_last_pay: "",
    loan_2_back_pay: "0",
    loan_2_lender: "",
    loan_2_loan_id: "",
    seller_notes: "",
    other_lien_amounts: "0",
    property_cma_value: "0",
    investor_price: "0",
    property_assdTtlValue: "",
    seller_wants_price: "0",
    zestimate: "",
    subd_county: "",
    property_subdName: "",
    property_legal_desc: "",
    property_lot_sqft: "",
    property_saleRecDate: "",
    property_parcel_id: "",
    property_tax_site: "",
    property_coolingType: "",
    property_heatingType: "",
    zestimate2: "",
    status: "New Lead",
  });

  const handleAction = async (action) => {
    setLoading((prev) => ({ ...prev, [action]: true }));
    const formData = new FormData();
    formData.append("owner", user?.app_id);

    let url = "";
    switch (action) {
      case "ai":
        formData.append("prop_address", lead?.property_address);
        formData.append("prop_state", lead?.property_state);
        formData.append("prop_city", lead?.property_city);
        url = "/newLeadAiQuery.php";
        break;
      default:
        setLoading((prev) => ({ ...prev, [action]: false }));
        return;
    }

    try {
      const response = await instance.post(url, formData);
      setLead((prevLead) => ({
        ...prevLead,
        ...response.data.data,
      }));
      await pulldata(user?.app_id);
      setError(null);
    } catch (e) {
      if (e.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        setError(`Error with action ${action}: ` + e.response.data.message);
        console.error(`Error with action ${action}: `, e.response.data.message);
      } else if (e.request) {
        // The request was made but no response was received
        setError("Error: No response received from server.");
        console.error("Error: ", e.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        setError("Error: " + e.message);
        console.error("Error: ", e.message);
      }
      setError(`Error with action ${action}:` + e.response.data.messagee);
      console.error(`Error with action ${action}:` + e.response.data.message);
    } finally {
      setLoading((prev) => ({ ...prev, [action]: false }));
      setIsAIConfirmOpen(false);
    }
  };

  const pullAlldata = async () => {
    try {
      const response = await instance.get(
        `/LoadHomes.php?u=${user?.app_id}&type=leads`,
      );
      if (Array.isArray(response.data)) {
        storage.set("user.Feed.Seller.Leads", JSON.stringify(response.data));
      }
    } catch (e) {
      console.error("Error fetching lead data after save:", e);
    }
  };

  const pullLeads = useCallback(
    async (pid) => {
      try {
        const response = await instance.get(
          `/LoadHomes.php?u=${pid}&type=view`,
        );
        const lead = response.data[0];
        onViewChange(views.editLead, "sellerleads", { lead });
      } catch (e) {
        if (e.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          setError("Error pulling lead details: " + e.response.data.message);
          console.error(
            "Error pulling lead details: ",
            e.response.data.message,
          );
        } else if (e.request) {
          // The request was made but no response was received
          setError("Error: No response received from server.");
          console.error("Error: ", e.request);
        } else {
          // Something happened in setting up the request that triggered an Error
          setError("Error: " + e.message);
          console.error("Error: ", e.message);
        }
      } finally {
        setError(null);
      }
    },
    [onViewChange],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const formData = new FormData();
    formData.append("owner", user?.app_id);

    for (const key in lead) {
      formData.append(key, lead[key]);
    }

    newPhotoFiles.forEach((file) => {
      formData.append("newPhotos[]", file, file.name);
    });

    try {
      const response = await instance.post("/addLead.php", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const newLead = response.data.lead;
      await pullAlldata();
      await pullLeads(newLead);
      await pulldata(user?.app_id);
      onViewChange("sellerleads", "sellerleads");
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "An unknown error occurred.";
      setError(`Error creating lead: ${errorMessage}`);
      console.error("Error creating lead:" + err);
    }
  };

  const handleSelectChange = useCallback((name, selectedValue) => {
    setLead((prevLead) => ({ ...prevLead, [name]: selectedValue }));
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setLead((prevLead) => ({ ...prevLead, [name]: value }));
  }, []);

  const handleFileChange = (files) => {
    if (files && files.length > 0) {
      const fileList = Array.from(files);
      setNewPhotoFiles((prev) => [...prev, ...fileList]);
      const previewUrls = fileList.map((file) => URL.createObjectURL(file));
      setNewPhotoPreviews((prev) => [...prev, ...previewUrls]);
    }
  };

  const removeNewPhoto = (indexToRemove) => {
    setNewPhotoFiles(
      newPhotoFiles.filter((_, index) => index !== indexToRemove),
    );
    setNewPhotoPreviews(
      newPhotoPreviews.filter((_, index) => index !== indexToRemove),
    );
  };

  const handlePlaceSelected = useCallback((place) => {
    const componentMap = {
      street_number: "long_name",
      route: "long_name", // Street name
      locality: "long_name", // City
      administrative_area_level_1: "short_name", // State
      postal_code: "long_name",
    };

    const addressData = {};

    place.address_components.forEach((component) => {
      const type = component.types[0];
      if (componentMap[type]) {
        addressData[type] = component[componentMap[type]];
      }
    });

    setLead((prevLead) => ({
      ...prevLead,
      property_address:
        `${addressData.street_number || ""} ${addressData.route || ""}`.trim(),
      property_city: addressData.locality || "",
      property_state: addressData.administrative_area_level_1 || "",
      property_zip: addressData.postal_code || "",
    }));
  }, []);

  return (
    <PageContent
      title="Seller Leads"
      subtitle="All Leads"
      thirdtitle="Create Lead"
    >
      {/* Hero Banner */}
      <div className="mx-8 mt-8 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-10 rounded-xl shadow-lg mb-8">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 rounded-lg">
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10.5 1.5H3.75A2.25 2.25 0 001.5 3.75v12.5A2.25 2.25 0 003.75 18.5h12.5a2.25 2.25 0 002.25-2.25V9.5m-15-4h9m-9 4h9m-9 4h6M10 1.5v4m4.5-4v4" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold">Create New Seller Lead</h1>
              <p className="text-orange-100 text-sm mt-1">Add a new property and owner information</p>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="mx-8 p-4 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-200 rounded-lg border border-red-200 dark:border-red-800 flex gap-3 items-start">
          <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <span>{error}</span>
        </div>
      )}

      <div className="px-8 pb-8">
        <div className="flex justify-between items-center mb-6 gap-3 flex-wrap">
          <button
            onClick={() => onViewChange("sellerleads", "sellerleads")}
            className="px-5 py-2 text-sm font-medium rounded-lg text-white bg-orange-600 hover:bg-orange-700 dark:bg-orange-700 dark:hover:bg-orange-600 transition-colors flex items-center gap-2"
          >
            ← Back to Leads
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className="px-5 py-2 text-sm font-medium rounded-lg text-white bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600 disabled:opacity-50 transition-colors flex items-center gap-2"
          >
            <svg
              fill="#fff"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              data-name="Layer 1"
            >
              <path d="M20.71,9.29l-6-6a1,1,0,0,0-.32-.21A1.09,1.09,0,0,0,14,3H6A3,3,0,0,0,3,6V18a3,3,0,0,0,3,3H18a3,3,0,0,0,3-3V10A1,1,0,0,0,20.71,9.29ZM9,5h4V7H9Zm6,14H9V16a1,1,0,0,1,1-1h4a1,1,0,0,1,1,1Zm4-1a1,1,0,0,1-1,1H17V16a3,3,0,0,0-3-3H10a3,3,0,0,0-3,3v3H6a1,1,0,0,1-1-1V6A1,1,0,0,1,6,5H7V8A1,1,0,0,0,8,9h6a1,1,0,0,0,1-1V6.41l4,4Z" />
            </svg>
            <p>{isLoading ? "Saving..." : "Save Lead"}</p>
          </button>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
              <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-200">
                Upload Photos
              </label>
              <div className="flex items-center justify-center w-full">
                <label
                  htmlFor="dropzone-file"
                  className="flex flex-col items-center justify-center w-full h-64 border-2 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-gray-200 group"
                >
                  <div className="flex flex-col items-center justify-center pt-5 pb-6 dark:bg-gray-800 dark:group-hover:bg-gray-700 dark:text-gray-200">
                    <svg
                      className="w-8 h-8 mb-4 text-gray-500 dark:text-gray-200"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 20 16"
                    >
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                      />
                    </svg>
                    <p className="mb-2 text-sm text-gray-500 dark:text-gray-200">
                      <span className="font-semibold dark:text-gray-200">
                        Click to upload
                      </span>{" "}
                      or drag and drop
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-200">
                      PNG, JPG
                    </p>
                  </div>
                  <input
                    id="dropzone-file"
                    type="file"
                    className="hidden"
                    multiple
                    onChange={(e) => handleFileChange(e.target.files)}
                  />
                </label>
              </div>
              <div className="grid gap-4 mt-4">
                {newPhotoPreviews.map((preview, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={preview}
                      alt={`New upload ${index + 1}`}
                      className="w-full h-32 object-cover rounded-lg"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-opacity flex items-center justify-center">
                      <button
                        type="button"
                        onClick={() => removeNewPhoto(index)}
                        className="text-white opacity-0 group-hover:opacity-100 transition-opacity bg-red-600 rounded-full p-2"
                      >
                        <svg
                          className="w-6 h-6"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          ></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
                <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400">
                  <CalendarIcon />
                </div>
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Lead Basics</h3>
              </div>
              <div className="space-y-5">
                <FormGroup
                  type="date"
                  label="Lead Date:"
                  name="lead_date"
                  value={lead.lead_date}
                  onChange={handleChange}
                />
                <FormGroup
                  label="Lead Source:"
                  name="input_source"
                  value={lead.input_source}
                  onChange={handleChange}
                  type="text"
                />
                <FormGroup
                  label="Lead Name:"
                  name="property_lead_name"
                  value={lead.property_lead_name}
                  onChange={handleChange}
                  type="text"
                />
              </div>
            </div>
            <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
                <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
                  <div className="p-2 rounded-lg bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400">
                    <MapPinIcon />
                  </div>
                  <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Property Location</h3>
                </div>
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Address</label>
                    <AutocompleteInput
                      onPlaceSelected={handlePlaceSelected}
                      placeholder="Enter property address"
                      name="property_address"
                      value={lead.property_address}
                      onChange={handleChange}
                      autoComplete="off"
                    />
                  </div>
                  <FormGroup
                    autoComplete="off"
                    label="City:"
                    name="property_city"
                    value={lead.property_city}
                    onChange={handleChange}
                    type="text"
                  />
                  <SelectGroup
                    autoComplete="off"
                    label="State:"
                    name="property_state"
                    value={lead.property_state}
                    onChange={handleSelectChange}
                    options={stateOptions}
                  />
                  <FormGroup
                    autoComplete="off"
                    label="Zip Code:"
                    name="property_zip"
                    value={lead.property_zip}
                    onChange={handleChange}
                    type="number"
                  />
                </div>
              </div>
            </form>
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
                <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"/><path fillRule="evenodd" d="M4 5a2 2 0 012-2 1 1 0 100-2H3a1 1 0 00-1 1v14a1 1 0 001 1h14a1 1 0 001-1V3a1 1 0 00-1-1h-3a1 1 0 100 2h3a1 1 0 011 1v14a1 1 0 001-1V5a2 2 0 00-2-2h-1a2 2 0 00-2 2v2H8V5zm0 5a1 1 0 000 2h6a1 1 0 100-2H4z" clipRule="evenodd"/></svg>
                </div>
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">AI Query</h3>
              </div>
              <div className="flex justify-center">
                <button
                  onClick={() => setIsAIConfirmOpen(true)}
                  disabled={
                    lead.property_address === ""
                      ? true
                      : loading.ai
                        ? false
                        : false
                  }
                  className="w-full sm:w-auto px-5 py-2 text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 transition-colors duration-200 flex items-center justify-center gap-2"
                >
                {" "}
                <svg
                  className="text-black-900 dark:text-gray-200"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1"
                  strokeLinecap="round"
                  strokeLinejoin="miter"
                >
                  <rect
                    x="9"
                    y="9"
                    width="6"
                    height="6"
                    fill="currentColor"
                    opacity="0.1"
                    rx="0"
                  ></rect>
                  <rect
                    x="9"
                    y="9"
                    width="6"
                    height="6"
                    fill="currentColor"
                    rx="0"
                  ></rect>
                  <rect x="5" y="5" width="14" height="14" rx="0"></rect>
                  <line x1="2" y1="9" x2="5" y2="9"></line>
                  <line x1="2" y1="15" x2="5" y2="15"></line>
                  <line x1="19" y1="9" x2="22" y2="9"></line>
                  <line x1="19" y1="15" x2="22" y2="15"></line>
                  <line x1="15" y1="2" x2="15" y2="5"></line>
                  <line x1="9" y1="2" x2="9" y2="5"></line>
                  <line x1="15" y1="19" x2="15" y2="22"></line>
                  <line x1="9" y1="19" x2="9" y2="22"></line>
                </svg>
                  <span>{loading.ai ? "Querying..." : "AI Query"}</span>
                </button>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
                <div className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400">
                  <UserIcon />
                </div>
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Owner Information</h3>
              </div>
              <div className="space-y-5">
                <FormGroup
                  type="text"
                  label="Owner Name:"
                  name="property_owner_name"
                  value={lead.property_owner_name}
                  onChange={handleChange}
                />
                <FormGroup
                  type="text"
                  label="Secondary Contact:"
                  name="secondary_contact"
                  value={lead.secondary_contact}
                  onChange={handleChange}
                />
                <FormGroup
                  type="text"
                  label="Mailing Address:"
                  name="property_address_mailing"
                  value={lead.property_address_mailing}
                  onChange={handleChange}
                />
                <FormGroup
                  type="tel"
                  label="Primary Number:"
                  name="mobile_number"
                  value={lead.mobile_number}
                  onChange={handleChange}
                />
                <FormGroup
                  type="tel"
                  label="Alternate Number:"
                  name="alternate_number"
                  value={lead.alternate_number}
                  onChange={handleChange}
                />
                <FormGroup
                  type="email"
                  label="Email Address:"
                  name="email_address"
                  value={lead.email_address}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
                <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400">
                  <ClockIcon />
                </div>
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Seller Situation</h3>
              </div>
              <div className="space-y-5">
                <FormGroup
                  type="text"
                  label="Minimum Asking Price:"
                  name="asking_price"
                  value={lead.asking_price}
                  onChange={handleChange}
                />
                <FormGroup
                  type="text"
                  label="Quickly Needs to Sell:"
                  name="need_to_sell_days"
                  value={lead.need_to_sell_days}
                  onChange={handleChange}
                />
                <FormGroup
                  type="text"
                  label="Needs Another Home:"
                  name="needs_home"
                  value={lead.needs_home}
                  onChange={handleChange}
                />
                <SelectGroup
                  label="MLS Listed:"
                  name="property_listed"
                  value={lead.property_listed}
                  onChange={handleSelectChange}
                  options={boolOptions}
                />
                <SelectGroup
                  label="Foreclosed:"
                  name="property_isForeclosed"
                  value={lead.property_isForeclosed}
                  onChange={handleSelectChange}
                  options={boolOptions}
                />
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
                <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400">
                  <ShieldIcon />
                </div>
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Escrow</h3>
              </div>
              <div className="space-y-5">
                <SelectGroup
                  label="Insurance is Escrowed:"
                  name="insur_escrowed"
                  value={lead.insur_escrowed}
                  onChange={handleSelectChange}
                  options={boolOptions}
                />
                <SelectGroup
                  label="Tax is Escrowed:"
                  name="tax_escrowed"
                  value={lead.tax_escrowed}
                  onChange={handleSelectChange}
                  options={boolOptions}
                />
                <SelectGroup
                  label="HOA/PMI/Other is Escrowed:"
                  name="other_escrowed"
                  value={lead.other_escrowed}
                  onChange={handleSelectChange}
                  options={boolOptions}
                />
              </div>
            </div>
          </div>
          {/* Middle Column */}
          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
                <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400">
                  <DollarIcon />
                </div>
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Financial Overview</h3>
              </div>
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="flex flex-col items-center">
                  <label className="font-medium text-gray-500 text-xs dark:text-gray-200 mb-2 uppercase">Debt</label>
                  <FormGroupSingle
                    type="number"
                    name="total_debt"
                    value={lead.total_debt}
                    onChange={handleChange}
                  />
                </div>
                <div className="flex flex-col items-center">
                  <label className="font-medium text-gray-500 text-xs dark:text-gray-200 mb-2 uppercase">Value</label>
                  <FormGroupSingle
                    type="number"
                    name="property_valued_price"
                    value={lead.property_valued_price}
                    onChange={handleChange}
                  />
                </div>
                <div className="flex flex-col items-center">
                  <label className="font-medium text-gray-500 text-xs dark:text-gray-200 mb-2 uppercase">Repairs</label>
                  <FormGroupSingle
                    type="number"
                    name="property_repair_costs"
                    value={lead.property_repair_costs}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="border-t border-gray-100 dark:border-gray-700 pt-6 space-y-5">
                <FormGroup
                  type="number"
                  label="CMA Estimate:"
                  name="property_cma_value"
                  value={lead.property_cma_value}
                  onChange={handleChange}
                />
                <FormGroup
                  type="number"
                  label="Internet Estimate:"
                  name="zestimate"
                  value={lead.zestimate}
                  onChange={handleChange}
                />
                <FormGroup
                  type="number"
                  label="Investor Estimate:"
                  name="investor_price"
                  value={lead.investor_price}
                  onChange={handleChange}
                />
                <FormGroup
                  type="number"
                  label="Tax Estimate:"
                  name="property_assdTtlValue"
                  value={lead.property_assdTtlValue}
                  onChange={handleChange}
                />
                <FormGroup
                  type="number"
                  label="Seller Estimate:"
                  name="seller_wants_price"
                  value={lead.seller_wants_price}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
                <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400">
                  <DocumentIcon />
                </div>
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Property Records</h3>
              </div>
              <div className="space-y-5">
                <FormGroup
                  type="text"
                  label="County:"
                  name="subd_county"
                  value={lead.subd_county}
                  onChange={handleChange}
                />
                <FormGroup
                  type="text"
                  label="Subdivision:"
                  name="property_subdName"
                  value={lead.property_subdName}
                  onChange={handleChange}
                />
                <FormGroup
                  type="text"
                  label="Legal Name:"
                  name="property_legal_desc"
                  value={lead.property_legal_desc}
                  onChange={handleChange}
                />
                <FormGroup
                  type="text"
                  label="Lot Sqft:"
                  name="property_lot_sqft"
                  value={lead.property_lot_sqft}
                  onChange={handleChange}
                />
                <FormGroup
                  type="date"
                  label="Deed Date:"
                  name="property_saleRecDate"
                  value={lead.property_saleRecDate}
                  onChange={handleChange}
                />
                <FormGroup
                  type="text"
                  label="Parcel ID:"
                  name="property_parcel_id"
                  value={lead.property_parcel_id}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
                <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                  <HomeIcon />
                </div>
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Property Details</h3>
              </div>
              <div className="space-y-5">
                <SelectGroup
                  label="Property Type:"
                  name="property_type"
                  value={lead.property_type}
                  onChange={handleSelectChange}
                  options={propertyTypeOptions}
                />
                <FormGroup
                  type="number"
                  label="Bedrooms:"
                  name="property_beds"
                  value={lead.property_beds}
                  onChange={handleChange}
                />
                <FormGroup
                  type="number"
                  label="Bathrooms:"
                  name="property_baths"
                  value={lead.property_baths}
                  onChange={handleChange}
                />
                <FormGroup
                  type="number"
                  label="Year Built:"
                  name="property_year_built"
                  value={lead.property_year_built}
                  onChange={handleChange}
                />
                <FormGroup
                  type="number"
                  label="Home Sqft:"
                  name="property_sqft"
                  value={lead.property_sqft}
                  onChange={handleChange}
                />
                <FormGroup
                  type="number"
                  label="Garage:"
                  name="property_garage"
                  value={lead.property_garage}
                  onChange={handleChange}
                />
                <FormGroup
                  type="number"
                  label="Stories:"
                  name="property_stories"
                  value={lead.property_stories}
                  onChange={handleChange}
                />
                <FormGroup
                  type="number"
                  label="Living Areas:"
                  name="property_living_areas"
                  value={lead.property_living_areas}
                  onChange={handleChange}
                />
                <SelectGroup
                  label="Has Pool:"
                  name="property_has_pool"
                  value={lead.property_has_pool}
                  onChange={handleSelectChange}
                  options={boolOptions}
                />
                <SelectGroup
                  label="Foundation:"
                  name="property_foundation"
                  value={lead.property_foundation}
                  onChange={handleSelectChange}
                  options={foundationOptions}
                />
                <SelectGroup
                  label="Occupancy:"
                  name="property_is_occupied"
                  value={lead.property_is_occupied}
                  onChange={handleSelectChange}
                  options={propertyOccupyOptions}
                />
                <SelectGroup
                  label="Condition:"
                  name="property_condition"
                  value={lead.property_condition}
                  onChange={handleSelectChange}
                  options={propertyConditionOptions}
                />
                <SelectGroup
                  label="Has HOA:"
                  name="property_has_hoa"
                  value={lead.property_has_hoa}
                  onChange={handleSelectChange}
                  options={boolOptions}
                />
              </div>
            </div>
          </div>
          {/* Right Column */}
          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
                <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400">
                  <LinkIcon />
                </div>
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Other Liens</h3>
              </div>
              <div className="space-y-5">
                <FormGroup
                  type="number"
                  label="Lien Amounts:"
                  name="other_lien_amounts"
                  value={lead.other_lien_amounts}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
                <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400">
                  <CreditCardIcon />
                </div>
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">First Mortgage</h3>
              </div>
              <div className="space-y-5">
                <SelectGroup
                  label="Has First Mortgage:"
                  name="bool_firstLoan"
                  value={lead.bool_firstLoan}
                  onChange={handleSelectChange}
                  options={boolOptions}
                />
                <FormGroup
                  type="date"
                  label="Origination Date:"
                  name="loan_1_origin"
                  value={lead.loan_1_origin}
                  onChange={handleChange}
                />
                <FormGroup
                  type="number"
                  label="Original Amount:"
                  name="loan_1_loan_amount"
                  value={lead.loan_1_loan_amount}
                  onChange={handleChange}
                />
                <FormGroup
                  type="number"
                  label="Interest Rate:"
                  name="loan_1_loan_intrest_rate"
                  value={lead.loan_1_loan_intrest_rate}
                  onChange={handleChange}
                />
                <FormGroup
                  type="number"
                  label="Loan Term Years:"
                  name="loan_1_loan_term"
                  value={lead.loan_1_loan_term}
                  onChange={handleChange}
                />
                <FormGroup
                  type="number"
                  label="Balance:"
                  name="loan_1_loan_balance"
                  value={lead.loan_1_loan_balance}
                  onChange={handleChange}
                />
                <SelectGroup
                  label="Type:"
                  name="loan_1_loan_type"
                  value={lead.loan_1_loan_type}
                  onChange={handleSelectChange}
                  options={loanTypeOptions}
                />
                <FormGroup
                  type="date"
                  label="Last Pay Date:"
                  name="loan_1_last_pay"
                  value={lead.loan_1_last_pay}
                  onChange={handleChange}
                />
                <FormGroup
                  type="number"
                  label="Back Pay Amount:"
                  name="loan_1_back_pay"
                  value={lead.loan_1_back_pay}
                  onChange={handleChange}
                />
                <FormGroup
                  type="text"
                  label="Lender Name:"
                  name="loan_1_lender"
                  value={lead.loan_1_lender}
                  onChange={handleChange}
                />
                <FormGroup
                  type="text"
                  label="Load ID:"
                  name="loan_1_loan_id"
                  value={lead.loan_1_loan_id}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
                <div className="p-2 rounded-lg bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400">
                  <CreditCardIcon />
                </div>
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Second Mortgage</h3>
              </div>
              <div className="space-y-5">
                <SelectGroup
                  label="Has Second Mortgage:"
                  name="bool_secondLoan"
                  value={lead.bool_secondLoan}
                  onChange={handleSelectChange}
                  options={boolOptions}
                />
                <FormGroup
                  type="date"
                  label="Origination Date:"
                  name="loan_2_origin"
                  value={lead.loan_2_origin}
                  onChange={handleChange}
                />
                <FormGroup
                  type="number"
                  label="Original Amount:"
                  name="loan_2_loan_amount"
                  value={lead.loan_2_loan_amount}
                  onChange={handleChange}
                />
                <FormGroup
                  type="number"
                  label="Interest Rate:"
                  name="loan_2_loan_intrest_rate"
                  value={lead.loan_2_loan_intrest_rate}
                  onChange={handleChange}
                />
                <FormGroup
                  type="number"
                  label="Loan Term Years:"
                  name="loan_2_loan_term"
                  value={lead.loan_2_loan_term}
                  onChange={handleChange}
                />
                <FormGroup
                  type="number"
                  label="Balance:"
                  name="loan_2_loan_balance"
                  value={lead.loan_2_loan_balance}
                  onChange={handleChange}
                />
                <SelectGroup
                  label="Type:"
                  name="loan_2_loan_type"
                  value={lead.loan_2_loan_type}
                  onChange={handleSelectChange}
                  options={loanTypeOptions}
                />
                <FormGroup
                  type="date"
                  label="Last Pay Date:"
                  name="loan_2_last_pay"
                  value={lead.loan_2_last_pay}
                  onChange={handleChange}
                />
                <FormGroup
                  type="number"
                  label="Back Pay Amount:"
                  name="loan_2_back_pay"
                  value={lead.loan_2_back_pay}
                  onChange={handleChange}
                />
                <FormGroup
                  type="text"
                  label="Lender Name:"
                  name="loan_2_lender"
                  value={lead.loan_2_lender}
                  onChange={handleChange}
                />
                <FormGroup
                  type="text"
                  label="Load ID:"
                  name="loan_2_loan_id"
                  value={lead.loan_2_loan_id}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
                <div className="p-2 rounded-lg bg-slate-100 dark:bg-slate-900/30 text-slate-600 dark:text-slate-400">
                  <PenIcon />
                </div>
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Seller Notes</h3>
              </div>
              <textarea
                className="w-full h-64 p-3 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                placeholder="Enter notes here..."
                name="seller_notes"
                onChange={handleChange}
                value={lead.seller_notes}
              />
            </div>
          </div>
        </div>
      </div>
      <Modal
        show={isSAIConfirmOpen}
        onClose={() => setIsAIConfirmOpen(false)}
        title="Confirm AI Query"
        footer={
          <div className="flex gap-4">
            <button
              onClick={() => {
                handleAction("ai");
              }}
              disabled={loading.ai}
              className="px-4 py-2 text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700"
            >
              {loading.ai ? "Quering..." : "Submit"}
            </button>
          </div>
        }
      >
        <p>
          This will use 1 AI Query Credit to find information for this lead. Are
          you sure you want to continue?
        </p>
      </Modal>
    </PageContent>
  );
};

export default EditLeadPageBlank;
